<template>
    <card v-if="arEnabled" cardBodyClasses="p-0">
        <b-row>
            <b-col cols="12">
                <h4 class="card-subtitle ar-inset" style="padding-top: 15px">
                    Accounts Receivable
                </h4>
            </b-col>
        </b-row>

        <template v-if="hasArViewPermission">
            <div v-if="loading" class="text-center py-3 ar-inset d-flex align-items-center justify-content-center"
                style="gap: 6px;">
                <b-spinner small />
                Loading account...
            </div>

            <div v-else-if="!accounts.length" class="text-center py-2 ar-inset">
                <div>No AR Account On File</div>
                <b-button v-if="canCreate" size="sm" variant="outline-primary" class="mt-2" @click="openCreate">
                    Create AR Account
                </b-button>
            </div>

            <div class="py-2 pb-0 ar-inset" v-else>
                <div v-for="acct in accounts" :key="acct.id" class="ar-account">
                    <b-row class="mb-1">
                        <b-col cols="7">
                            <strong>{{ acct.accountNumber }}</strong>
                            <b-badge :variant="statusVariant(acct.status)" class="ml-1">
                                {{ statusLabel(acct.status) }}
                            </b-badge>
                            <b-badge v-if="acct.autoPayEnabled" variant="info" class="ml-1">
                                Auto-Pay
                            </b-badge>
                        </b-col>
                        <b-col cols="5" class="text-right">
                            <strong>Balance:</strong> {{ currency(acct.currentBalance) }}
                        </b-col>
                    </b-row>

                    <b-row v-if="noteText(acct.notes)" class="mb-1">
                        <b-col cols="12">
                            <div class="d-flex align-items-center text-muted" style="gap: 4px;">
                                <i class="fas fa-sticky-note"></i>
                                <small class="text-truncate ar-note-hint" v-b-tooltip.hover
                                    :title="noteText(acct.notes)">{{ noteText(acct.notes) }}</small>
                            </div>
                        </b-col>
                    </b-row>

                    <b-table-simple small bordered class="mb-1">
                        <thead>
                            <tr>
                                <th>Current</th>
                                <th>31-60</th>
                                <th>61-90</th>
                                <th>91-120</th>
                                <th>120+</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{{ currency(agingFor(acct.id).agingCurrent) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket1) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket2) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket3) }}</td>
                                <td>{{ currency(agingFor(acct.id).agingBucket4Plus) }}</td>
                            </tr>
                        </tbody>
                    </b-table-simple>

                    <div class="d-flex flex-wrap ar-actions">
                        <b-button v-if="canPostPayment" size="sm" variant="primary"
                            @click="openPostPayment(acct.id)">
                            Post Payment
                        </b-button>
                        <b-button v-if="canPostAdjustment" size="sm" variant="primary"
                            @click="openAdjustment(acct.id)">
                            Post Adjustment
                        </b-button>
                        <b-button v-if="canWriteOff" size="sm" variant="danger"
                            @click="openWriteOff(acct.id)">
                            Write Off Balance
                        </b-button>
                        <b-button v-if="showAutoPay" size="sm"
                            :variant="acct.autoPayEnabled ? 'success' : 'primary'"
                            @click="openAutoPay(acct.id)">
                            Auto-Pay
                        </b-button>
                        <b-button v-if="canManage" size="sm" variant="secondary"
                            @click="openManage(acct.id)">
                            Manage
                        </b-button>
                    </div>

                    <div class="text-center mt-1">
                        <b-button variant="link" size="sm" class="ar-ledger-toggle text-primary"
                            :aria-expanded="isLedgerExpanded(acct.id) ? 'true' : 'false'"
                            :aria-controls="`ar-ledger-${acct.id}`" @click="toggleLedger(acct.id)">
                            <i class="fas fa-fw" :class="ledgerChevronClass(acct.id)"></i> Ledger
                        </b-button>
                    </div>

                    <b-collapse :id="`ar-ledger-${acct.id}`" :visible="isLedgerExpanded(acct.id)"
                        role="tabpanel" @input="onLedgerVisibilityChange(acct.id, $event)">
                        <b-table-simple small responsive table-class="mb-0 ar-ledger-table" class="mb-0 mt-1">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Reason</th>
                                    <th>Notes</th>
                                    <th class="text-right">Amount</th>
                                    <th v-if="canVoidPayment"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="!ledgerFor(acct.id).length">
                                    <td :colspan="canVoidPayment ? 6 : 5" class="text-center">No transactions</td>
                                </tr>
                                <tr v-for="t in ledgerFor(acct.id)" :key="t.id">
                                    <td>{{ formatDate(t.created) }}</td>
                                    <td>{{ transactionTypeLabel(t.transactionType) }}</td>
                                    <td>{{ ledgerReason(t) }}</td>
                                    <td>
                                        <span v-if="noteText(t.notes)" class="ar-note" v-b-tooltip.hover
                                            :title="noteText(t.notes)">{{ noteText(t.notes) }}</span>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                    <td class="text-right">{{ currency(t.amount) }}</td>
                                    <td v-if="canVoidPayment" class="text-right">
                                        <b-button v-if="voidablePayment(t)" size="sm" variant="outline-danger"
                                            @click="openVoid(t)">
                                            Void
                                        </b-button>
                                    </td>
                                </tr>
                            </tbody>
                        </b-table-simple>
                    </b-collapse>
                </div>
            </div>
        </template>

        <b-alert :show="!hasArViewPermission" variant="warning" class="my-2" style="margin: 15px;">
            <strong>
                You do not have permissions to view Accounts Receivable.
            </strong>
        </b-alert>

        <ARAccountManageModal v-model="showManageModal" :patient="patient" :account-id="manageAccountId"
            @saved="onSaved" />
        <ARPostPaymentModal v-model="showPostPaymentModal" :account-id="actionAccountId"
            :card-payments-enabled="arPortalPaymentCC" @saved="onSaved" />
        <ARPostAdjustmentModal v-model="showAdjustmentModal" :account-id="actionAccountId" @saved="onSaved" />
        <ARWriteOffModal v-model="showWriteOffModal" :account-id="actionAccountId" @saved="onSaved" />
        <ARVoidPaymentModal v-model="showVoidModal" :payment="voidTarget" @saved="onSaved" />
        <ARAutoPayModal v-model="showAutoPayModal" :account-id="autoPayAccountId"
            :auto-pay-available="arAutoPayEnabled && arPortalPaymentCC" @saved="onSaved" />
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import {
    ARAccountSummary,
    ARAging,
    ARLedgerEntry,
    ARPaymentView,
    arAccountStatusLabel,
    arAccountStatusVariant,
    arTransactionTypeLabel,
} from "@/models/AR";
import TextUtils from "@/util/TextUtils";
import axios from "axios";
import moment from "moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ARPermissionsMixin from "@/mixins/ARPermissionsMixin";
import ARAccountManageModal from "@/components/Patient/ARAccountManageModal.vue";
import ARPostPaymentModal from "@/components/Patient/ARPostPaymentModal.vue";
import ARPostAdjustmentModal from "@/components/Patient/ARPostAdjustmentModal.vue";
import ARWriteOffModal from "@/components/Patient/ARWriteOffModal.vue";
import ARVoidPaymentModal from "@/components/Patient/ARVoidPaymentModal.vue";
import ARAutoPayModal from "@/components/Patient/ARAutoPayModal.vue";

@Component({
    name: "AccountsReceivableCard",
    components: {
        ARAccountManageModal,
        ARPostPaymentModal,
        ARPostAdjustmentModal,
        ARWriteOffModal,
        ARVoidPaymentModal,
        ARAutoPayModal,
    },
})
export default class AccountsReceivableCard extends Mixins(ARPermissionsMixin) {
    @Prop() private patient!: Patient;

    private arEnabled = false;
    private arAutoPayEnabled = false;
    private arPortalPaymentCC = false;
    private loading = false;
    private accounts: ARAccountSummary[] = [];
    private agingByAccount: Record<number, ARAging> = {};
    private ledgerByAccount: Record<number, ARLedgerEntry[]> = {};
    // Which account's ledger is expanded. Owned here rather than left to b-collapse's own state:
    // posting an action reloads the accounts, and that re-render drops an uncontrolled collapse
    // back to closed — which would make the user reopen the ledger after every post, including to
    // reach the next Void. One at a time, so a single id rather than a set.
    private expandedLedgerAccountId: number | null = null;
    private paymentByTransaction: Record<number, ARPaymentView> = {};
    private showManageModal = false;
    private manageAccountId: number | null = null;
    // The post/adjust/write-off modals each act on one account and only one can be open at a
    // time, so they share the account the action was launched from.
    private actionAccountId: number | null = null;
    private showPostPaymentModal = false;
    private showAdjustmentModal = false;
    private showWriteOffModal = false;
    private showVoidModal = false;
    private voidTarget: ARPaymentView | null = null;
    private showAutoPayModal = false;
    private autoPayAccountId: number | null = null;

    get hasArViewPermission(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/View");
    }

    get canCreate(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Create");
    }

    get canManage(): boolean {
        const managePermissions = [
            "Accounts Receivable/Account/Edit",
            "Accounts Receivable/Account/Close",
            "Accounts Receivable/Credit Limit/Override Account Limit",
        ];
        return this.$permissions.some(p => managePermissions.includes(p.permissionCodeName));
    }

    openCreate(): void {
        this.manageAccountId = null;
        this.showManageModal = true;
    }

    openManage(accountId: number): void {
        this.manageAccountId = accountId;
        this.showManageModal = true;
    }

    // Auto-pay enrollment is an account edit, and only surfaces when the pharmacy has the
    // auto-pay feature enabled. The endpoint enforces the same rules server-side.
    get showAutoPay(): boolean {
        return this.arAutoPayEnabled
            && this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Edit");
    }

    openAutoPay(accountId: number): void {
        this.autoPayAccountId = accountId;
        this.showAutoPayModal = true;
    }

    openPostPayment(accountId: number): void {
        this.actionAccountId = accountId;
        this.showPostPaymentModal = true;
    }

    openAdjustment(accountId: number): void {
        this.actionAccountId = accountId;
        this.showAdjustmentModal = true;
    }

    openWriteOff(accountId: number): void {
        this.actionAccountId = accountId;
        this.showWriteOffModal = true;
    }

    openVoid(entry: ARLedgerEntry): void {
        const payment = this.voidablePayment(entry);
        if (!payment) return;
        this.voidTarget = payment;
        this.showVoidModal = true;
    }

    async onSaved(): Promise<void> {
        // The open ledger stays open across the reload, so refetch it rather than dropping it —
        // clearing alone would leave it reading "No transactions". Any other account's cached
        // ledger is simply discarded and refetched if the user opens it again.
        const openLedgerAccountId = this.expandedLedgerAccountId;
        this.ledgerByAccount = {};
        this.agingByAccount = {};
        this.paymentByTransaction = {};
        await this.loadAccounts();
        if (openLedgerAccountId !== null) await this.loadLedger(openLedgerAccountId);
    }

    async mounted(): Promise<void> {
        await this.loadConfiguration();
        if (this.arEnabled && this.hasArViewPermission && this.patient?.id) {
            await this.loadAccounts();
        }
    }

    @Watch("patient")
    async onPatientChanged(newPatient: Patient, oldPatient: Patient): Promise<void> {
        if (newPatient?.id === oldPatient?.id) return;
        this.accounts = [];
        this.agingByAccount = {};
        this.ledgerByAccount = {};
        this.expandedLedgerAccountId = null;
        if (this.arEnabled && this.hasArViewPermission && newPatient?.id) {
            await this.loadAccounts();
        }
    }

    private async loadConfiguration(): Promise<void> {
        // These flags are independent, so fetch them together rather than serially.
        const [arEnabled, arAutoPayEnabled, arPortalPaymentCC] = await Promise.all([
            axios.get<boolean>("/configuration/enableAR").then(r => r.data === true).catch(() => false),
            axios.get<boolean>("/configuration/enableArAutoPay").then(r => r.data === true).catch(() => false),
            axios.get<boolean>("/configuration/arPortalPaymentCC").then(r => r.data === true).catch(() => false),
        ]);
        this.arEnabled = arEnabled;
        this.arAutoPayEnabled = arAutoPayEnabled;
        this.arPortalPaymentCC = arPortalPaymentCC;
    }

    private async loadAccounts(): Promise<void> {
        if (!this.patient?.id) return;
        try {
            this.loading = true;
            const response = await axios.get<ARAccountSummary[]>(`/AR/patients/${this.patient.id}/accounts`);
            this.accounts = response.data ?? [];
            await Promise.all(this.accounts.map(a => this.loadAging(a.id)));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    private async loadAging(accountId: number): Promise<void> {
        try {
            const response = await axios.get<ARAging>(`/AR/accounts/${accountId}/aging`);
            this.$set(this.agingByAccount, accountId, response.data ?? {});
        } catch {
            // Aging is non-critical for the card; ignore failures.
        }
    }

    async toggleLedger(accountId: number): Promise<void> {
        if (this.expandedLedgerAccountId === accountId) {
            this.expandedLedgerAccountId = null;
            return;
        }
        this.expandedLedgerAccountId = accountId;
        if (this.ledgerByAccount[accountId]) return;
        await this.loadLedger(accountId);
    }

    // b-collapse can also change its own visibility. Track that so the two can never disagree
    // about whether the ledger is open.
    onLedgerVisibilityChange(accountId: number, visible: boolean): void {
        if (visible) this.expandedLedgerAccountId = accountId;
        else if (this.expandedLedgerAccountId === accountId) this.expandedLedgerAccountId = null;
    }

    isLedgerExpanded(accountId: number): boolean {
        return this.expandedLedgerAccountId === accountId;
    }

    // Points right when closed and down when open — the usual disclosure affordance. Without it
    // the toggle read as a static label, which is how it was being missed.
    ledgerChevronClass(accountId: number): string {
        return this.isLedgerExpanded(accountId) ? "fa-chevron-down" : "fa-chevron-right";
    }

    private async loadLedger(accountId: number): Promise<void> {
        try {
            // Payments carry the method/check#/reference used to label payment rows in the ledger.
            const [ledgerResponse, paymentsResponse] = await Promise.all([
                axios.get<ARLedgerEntry[]>(`/AR/accounts/${accountId}/ledger`),
                axios.get<ARPaymentView[]>(`/AR/accounts/${accountId}/payments`),
            ]);
            this.$set(this.ledgerByAccount, accountId, ledgerResponse.data ?? []);
            for (const payment of paymentsResponse.data ?? []) {
                if (payment.arTransactionId != null) {
                    this.$set(this.paymentByTransaction, payment.arTransactionId, payment);
                }
            }
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }
    }

    // The reason column shows the transaction's reason code, falling back to the payment method
    // (Cash, Check <#>, CC <ref>) for payment rows that have no reason of their own.
    ledgerReason(entry: ARLedgerEntry): string {
        if (entry.reasonCode) return entry.reasonCode;
        if (entry.transactionType === 9 && entry.id != null) {
            const payment = this.paymentByTransaction[entry.id];
            if (payment) {
                switch (payment.paymentMethod) {
                    case 1: return "Cash";
                    case 2: return `Check ${payment.checkNumber ?? ""}`.trim();
                    case 3: return `CC ${payment.referenceId ?? ""}`.trim();
                }
            }
        }
        return "";
    }

    // Notes are free text, so a whitespace-only note has to read as no note at all — otherwise
    // the cell renders an empty tooltip target the user can hover but never read.
    noteText(value?: string | null): string {
        return value?.trim() ?? "";
    }

    // Void targets one specific payment, so it is offered on the ledger row that payment created.
    // Voiding writes its own refund row, which is not itself voidable, and a payment already
    // voided has no second void to give.
    voidablePayment(entry: ARLedgerEntry): ARPaymentView | null {
        if (entry.transactionType !== 9 || entry.id == null) return null;
        const payment = this.paymentByTransaction[entry.id];
        return payment?.status === 1 ? payment : null;
    }

    agingFor(accountId: number): ARAging {
        return this.agingByAccount[accountId] ?? {};
    }

    ledgerFor(accountId: number): ARLedgerEntry[] {
        return this.ledgerByAccount[accountId] ?? [];
    }

    statusLabel(status?: number): string {
        return arAccountStatusLabel(status);
    }

    transactionTypeLabel(type?: number): string {
        return arTransactionTypeLabel(type);
    }

    statusVariant(status?: number): string {
        return arAccountStatusVariant(status);
    }

    currency(value?: number | null): string {
        return TextUtils.currencyFormatter(value ?? 0);
    }

    formatDate(date?: string): string {
        if (!date) return "";
        return moment.utc(date).local().format("L");
    }
}
</script>

<style scoped>
/* The card sits in a one-third-width dashboard column, so notes are clipped to a single line
   and the full text is revealed by the hover tooltip. */
.ar-note {
    display: inline-block;
    max-width: 9em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
    cursor: help;
}

.ar-note-hint {
    cursor: help;
}

/* One inset shared by the title and the body so the card's content lines up on both edges. */
.ar-inset {
    padding-left: 15px;
    padding-right: 15px;
}

/* Space between accounts rather than after each one, so the last account leaves no dead space
   under the ledger toggle. */
.ar-account + .ar-account {
    margin-top: 1rem;
}

/* The actions fill the card's width and share it evenly. min-width forces a wrap to the next row
   rather than letting a label like "Write Off Balance" squeeze onto two lines, and every row of
   buttons still spans the full width. */
.ar-actions {
    gap: 6px;
}

/* The theme gives buttons a bottom margin, which stacks on top of the flex gap and leaves a
   visible step between wrapped rows. The gap owns the spacing here, so clear the margin. */
.ar-actions > .btn {
    flex: 1 1 auto;
    min-width: 8.5em;
    margin: 0;
}

/* Sits under the actions on its own line, so the label can no longer be squeezed by them. Weighted
   rather than muted so it reads as the control it is. */
.ar-ledger-toggle {
    text-decoration: none;
    box-shadow: none;
    font-weight: 600;
    /* Same theme bottom margin as the action buttons; it would otherwise leave a gap between the
       toggle and the ledger it opens. */
    margin: 0;
}

.ar-ledger-toggle:hover,
.ar-ledger-toggle:focus {
    text-decoration: none;
}

/* Dense reference data in a narrow column: a notch smaller and tighter than the default reads
   better and fits more of the ledger on screen before it has to scroll. */
.ar-ledger-table {
    font-size: 0.85em;
}

.ar-ledger-table th,
.ar-ledger-table td {
    padding: 0.25rem 0.4rem;
    vertical-align: middle;
}
</style>
